import { useState, useEffect } from 'react';
import { useLanguage } from '../../providers/preferenceProvider';
import { useConfig } from '../../providers/configProvider';
import { useToast } from '../../providers/toastProvider';
import '../../css/overlay.css';
import SearchableSelect from './searchableSelect';
import { createRoom, updateRoom, deleteRoom, updateRoomImage } from '../../api/room-api';
import { getRoomTypes } from '../../api/room-type-api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faUser, faClock, faSquareCheck, faCoins, faStopwatch, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { ACL } from '../../acls';
import UploadImage from './imageUpload';

const EditRoom = ({ isOpen, onClose, room, createNew, onRefresh, editable = true }) => {
  const { t } = useLanguage();
  const { isAuthorized } = useConfig();
  const { showToast, showActionToast, showLoading, hideLoading } = useToast();
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState('');
  const [floor, setFloor] = useState(1);
  const [openTimeHour, setOpenTimeHour] = useState('00');
  const [openTimeMinute, setOpenTimeMinute] = useState('00');
  const [closeTimeHour, setCloseTimeHour] = useState('00');
  const [closeTimeMinute, setCloseTimeMinute] = useState('00');
  const [active, setActive] = useState(true);
  const [type, setType] = useState('');
  const [credits, setCredits] = useState(0);
  const [interval, setInterval] = useState(0);
  const [maxBookingTime, setMaxBookingTime] = useState(0);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [image, setImage] = useState('');
  const [ImageOverlayOpen, setImageOverlayOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);


  useEffect(() => {
    if (!isOpen) return;
    if (room) {
      setName(room.name);
      setCapacity(room.capacity);
      setFloor(room.floor);
      setSplitTime(room.openTime, setOpenTimeHour, setOpenTimeMinute);
      setSplitTime(room.closeTime, setCloseTimeHour, setCloseTimeMinute);
      setActive(room.active);
      setType(room.typeId);
      setCredits(room.credits || 0);
      setInterval(room.interval);
      setMaxBookingTime(room.maxBookingTime);
      setImage(room.image || '');
    } else {
      setImage('');
    }
  }, [isOpen, room]);

  useEffect(() => {
    getRoomTypes().then((response) => {
      setTypeOptions(response.data);
    }).catch(() => {
      showToast("Error fetching room types", "error");
    });
  }, []);

  if (!isOpen) return null;

  const closeOverlay = () => {
    onClose();
  }

  const saveChange = async () => {
    showLoading();

    const newErrors = [];
    if (!name) newErrors.push("name");
    if (!capacity || capacity <= 0) newErrors.push("capacity");
    if (!floor) newErrors.push("floor");
    if (!openTimeHour || !openTimeMinute) newErrors.push("openTime");
    if (!closeTimeHour || !closeTimeMinute) newErrors.push("closeTime");
    if (openTimeHour < 0 || openTimeHour > 23 || openTimeMinute < 0 || openTimeMinute > 59) newErrors.push("openTime");
    if (closeTimeHour < 0 || closeTimeHour > 23 || closeTimeMinute < 0 || closeTimeMinute > 59) newErrors.push("closeTime");
    if (!type || !typeOptions.some((option) => option.id === type)) newErrors.push("type");
    if (!interval) newErrors.push("interval");
    if (credits && credits < 0) newErrors.push("credits");
    if (!maxBookingTime || maxBookingTime < 1) newErrors.push("maxBookingTime");
    setFieldErrors(newErrors);

    if (newErrors.length > 0) {
      showToast(t.toast.fill_all_fields, 'error');
      hideLoading();
      return;
    }

    try {
      if (createNew) {
        const createdRoom = await createRoom(name?.trim(), capacity, floor, openTimeHour, openTimeMinute, closeTimeHour, closeTimeMinute, active, type, interval, credits, maxBookingTime);
        if (image) await updateRoomImage(createdRoom.data.id, uploadedFile);
      } else {
        await updateRoom(room.id, name?.trim(), capacity, floor, openTimeHour, openTimeMinute, closeTimeHour, closeTimeMinute, active, type, interval, credits);
      }
    
      onRefresh();
      hideLoading();
      closeOverlay();
      if (createNew) showToast(t.room + " " + t.toast.created_success, 'success');
      else showToast(t.room + " " + t.toast.updated_success, 'success');
      hideLoading();
    } catch (error) {
      hideLoading();
      console.error("Error saving room:", error);
    }
  }

  const deleteComfirmation = () => {
    showActionToast(
      t.toast.confirm_delete_general.replace('{ph_name}', room.name),
      handleDeleteRoom,
      null
    );
  }

  const handleDeleteRoom = () => {
    showLoading();
    deleteRoom(room.id).then(() => {
      onRefresh();
      hideLoading();
      closeOverlay();
      showToast(t.room + " " + t.toast.deleted_success, 'success');
    }).catch(() => {
      hideLoading();
    });
  }

  function handleTypeChange(selectedType) {
    setType(selectedType);
    const type = typeOptions.find(option => option.id === selectedType);
    if (type) {
      setOptional(type.capacity, setCapacity);
      setOptional(type.credits, setCredits)
      setOptional(type.interval, setInterval);
      setOptional(type.maxBookingTime, setMaxBookingTime);
      setSplitTime(type.openTime, setOpenTimeHour, setOpenTimeMinute);
      setSplitTime(type.closeTime, setCloseTimeHour, setCloseTimeMinute);
    }
  }

  function setSplitTime(toSplit, setHours, setMinutes){
    setOptional(toSplit?.split(':')[0], setHours);
    setOptional(toSplit?.split(':')[1], setMinutes);
  }
  
  function setOptional(value, set) {
    if (value) {
      set(value);
    }
  }
  
  const handleSaveImage = async (file) => {
    showLoading();
    try {
      if (!createNew) await updateRoomImage(room.id, file);
      else setUploadedFile(file);
      setImage(URL.createObjectURL(file));
      setImageOverlayOpen(false);
      onRefresh();
      hideLoading();
    } catch {
      hideLoading();
    }
  };

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-header">{!editable ? '' : createNew ? t.add : t.edit} {t.room}</div>
        <div className="overlay-admin-form flex flex-col p-4">

          <div className="flex flex-row items-center gap-4">
            { (image && image !== '') &&
            <img src={image} alt="room" className="w-20 h-20 object-cover rounded-md border" />
            }
            {editable && <button className="btn" type="button" onClick={() => setImageOverlayOpen(true)} disabled={!editable}>{t.edit + " " + t.image}</button>}
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="name" className="w-32"><FontAwesomeIcon icon={faPen} /> {t.name}</label>
            <input id="name" name="name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t.name + "..."} className={"overlay-admin-input" + (fieldErrors.includes("name") ? " input-error" : "")} disabled={!editable}/>
          </div>

          <SearchableSelect label={t.room_type} icon="type" options={typeOptions} value={type} onChange={handleTypeChange} error={fieldErrors.includes("type")} />

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="capacity" className="w-32"><FontAwesomeIcon icon={faUser} /> {t.capacity}</label>
            <input type="number" id="capacity" name="capacity" value={capacity} onChange={(e) => setCapacity(e.target.value)} placeholder={t.capacity + "..."} className={"overlay-admin-input" + (fieldErrors.includes("capacity") ? " input-error" : "")} disabled={!editable}/>
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="floor" className="w-32"><FontAwesomeIcon icon={faLayerGroup} /> {t.floor}</label>
            <input type="number" id="floor" name="floor" value={floor} onChange={(e) => setFloor(e.target.value)} placeholder={t.floor + "..."} className={"overlay-admin-input" + (fieldErrors.includes("floor") ? " input-error" : "")} disabled={!editable}/>
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="openTime" className="w-32"><FontAwesomeIcon icon={faClock} /> {t.opening_time}</label>
            <input type="number" min={0} max={23} id="openTimeHour" name="openTimeHour" value={openTimeHour} onChange={(e) => setOpenTimeHour(e.target.value)} className={"overlay-admin-time-input" + (fieldErrors.includes("openTime") ? " input-error" : "")} disabled={!editable}/>
              :
            <input type="number" min={0} max={59} id="openTimeMinute" name="openTimeMinute" value={openTimeMinute} onChange={(e) => setOpenTimeMinute(e.target.value)} className={"overlay-admin-time-input" + (fieldErrors.includes("openTime") ? " input-error" : "")} disabled={!editable}/>
          </div>

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="closeTimeHour" className="w-32"><FontAwesomeIcon icon={faClock} /> {t.closing_time}</label>
            <input type="number" min={0} max={23} id="closeTimeHour" name="closeTimeHour" value={closeTimeHour} onChange={(e) => setCloseTimeHour(e.target.value)} className={"overlay-admin-time-input" + (fieldErrors.includes("closeTime") ? " input-error" : "")} disabled={!editable}/>
              :
            <input type="number" min={0} max={59} id="closeTimeMinute" name="closeTimeMinute" value={closeTimeMinute} onChange={(e) => setCloseTimeMinute(e.target.value)} className={"overlay-admin-time-input" + (fieldErrors.includes("closeTime") ? " input-error" : "")} disabled={!editable}/>
          </div>

          {editable && <div className="flex flex-row items-center gap-4">
            <label htmlFor="active" className="w-32"><FontAwesomeIcon icon={faSquareCheck} /> {t.active}</label>
            <input type="checkbox" id="active" name="active" checked={active} onChange={(e) => setActive(e.target.checked)} className="overlay-admin-checkbox"/>
          </div>}

          <div className="flex flex-row items-center gap-4">
            <div className="w-32">
              <FontAwesomeIcon icon={faCoins} className='mr-3'/>
              <label>Cost</label>
            </div>
            
            <input 
              id='credits' 
              name='credits' 
              required={true}
              type="number" 
              className={"overlay-admin-time-input" + (fieldErrors.includes("credits") ? " input-error" : "")}
              step="1" 
              pattern="\d*" 
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
            />
            <span className="text-sm text-gray-600">credits per interval</span>
          </div>

          <div className="flex flex-row items-center gap-4">
            <div className="w-32">
              <FontAwesomeIcon icon={faStopwatch} className='mr-3'/>
              <label>Interval</label>
            </div>
            
            <input 
              id='interval' 
              name='interval' 
              required={true}
              type="number" 
              className={"overlay-admin-time-input" + (fieldErrors.includes("interval") ? " input-error" : "")}
              step="1" 
              pattern="\d*"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
            />
            <span className="text-sm text-gray-500">minutes</span>
          </div>

          <div className="flex flex-row items-center gap-4">
            <div className="w-32">
              <FontAwesomeIcon icon={faStopwatch} className='mr-3'/>
              <label>Limit</label>
            </div>
            
            <input 
              id='maxBookingTime' 
              name='maxBookingTime' 
              required={true}
              type="number" 
              className={"overlay-admin-time-input" + (fieldErrors.includes("maxBookingTime") ? " input-error" : "")}
              step="1"
              pattern="\d*"
              value={maxBookingTime}
              onChange={(e) => setMaxBookingTime(e.target.value)}
            />
            <span className="text-sm text-gray-500">minutes</span>
          </div>

        </div>
        <div className="flex flex-row  gap-2 justify-between">
          <div className='flex flex-row gap-2 align-items-center'>
            {(editable && !createNew) && (
              <button className="btn" onClick={deleteComfirmation} disabled={!isAuthorized(ACL.ROOM)}>{t.delete + " " + t.room}</button>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <button className="btn-close" onClick={closeOverlay}>{t.close}</button>
            {editable ? <button className="btn" onClick={saveChange}>{t.confirm}</button> : null}
          </div>
        </div>

        <UploadImage
          isOpen={ImageOverlayOpen}
          onClose={() => setImageOverlayOpen(false)}
          handleSaveImage={handleSaveImage}
          image={image}
        />
      </div>
    </div>
  );
};

export default EditRoom;